import { Copy, Check, ExternalLink, Loader2, Clock } from 'lucide-react';

interface AndroidInstructionsProps {
  onOpenInBrowser: () => void;
  onCopyLink: () => void;
  copied: boolean;
  copyError: boolean;
  isLoading?: boolean;
  countdown: number | null;
  formatCountdown: (seconds: number) => string;
  tokenFallbackUsed?: boolean;
}

export function AndroidInstructions({
  onOpenInBrowser,
  onCopyLink,
  copied,
  isLoading,
  countdown,
  formatCountdown,
  tokenFallbackUsed,
}: AndroidInstructionsProps) {
  const hasActiveToken = countdown !== null && countdown > 0;

  return (
    <div className="w-full max-w-xs space-y-4">
      {/* Countdown timer - always visible when token is active */}
      {hasActiveToken && !tokenFallbackUsed && (
        <div className="flex items-center justify-center gap-2 py-2 px-4 bg-card/80 border border-border rounded-lg">
          <Clock className="w-4 h-4 text-amber-400" />
          <span className="text-sm text-foreground/80">
            Link ready · expires in{' '}
            <span className="text-amber-400 font-mono font-semibold">
              {formatCountdown(countdown)}
            </span>
          </span>
        </div>
      )}

      {/* Primary action: Open in Chrome */}
      <h1 className="sr-only">Open in Chrome</h1>
      <button
        onClick={onOpenInBrowser}
        disabled={isLoading}
        className="w-full flex items-center justify-center gap-3 bg-amber-500 hover:bg-amber-400 disabled:bg-amber-500/50 disabled:cursor-wait text-slate-900 font-semibold py-4 px-6 rounded-xl transition-colors"
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Opening...
          </>
        ) : (
          <>
            <ExternalLink className="w-5 h-5" />
            Open in Chrome
          </>
        )}
      </button>

      {/* Divider */}
      <div className="flex items-center gap-4">
        <div className="flex-1 h-px bg-muted" />
        <span className="text-muted-foreground/70 text-sm">or</span>
        <div className="flex-1 h-px bg-muted" />
      </div>

      {/* Secondary action: Copy Link */}
      <button
        onClick={onCopyLink}
        disabled={isLoading}
        className={`w-full flex items-center justify-center gap-3 font-medium py-3 px-6 rounded-xl transition-all border ${
          copied
            ? 'bg-green-500/20 border-green-500/50 text-green-400'
            : 'bg-muted hover:bg-muted/80 disabled:bg-muted/50 disabled:cursor-wait text-white border-border'
        }`}
      >
        {isLoading ? (
          <>
            <Loader2 className="w-5 h-5 animate-spin" />
            Preparing...
          </>
        ) : copied ? (
          <>
            <Check className="w-5 h-5" />
            Copied! Paste in Chrome
          </>
        ) : (
          <>
            <Copy className="w-5 h-5" />
            Copy Link
          </>
        )}
      </button>

      {/* Success message after copy */}
      {copied && hasActiveToken && (
        <p className="text-center text-green-400 text-sm font-medium">
          ✓ Link copied! Open Chrome and paste it there
        </p>
      )}

      {/* Login reminder - contextual based on token */}
      <p className="text-center text-muted-foreground/70 text-xs mt-4">
        {hasActiveToken && !tokenFallbackUsed
          ? 'You\'ll stay logged in when you open the link'
          : 'You may need to log in again in Chrome'}
      </p>
    </div>
  );
}
